import { transaction } from 'mobx';
import { ISlideGuidesService } from '@src/slide/application/service/guides-manager/slide-guides-service.interface';
import {
    EPpGuideOrientation, EUpdateGuidesType, IGuide,
} from '@src/slide/domain/ui-model/element-modeles/Interface/guide-model.interface';
import { Guide } from '@src/slide/domain/ui-model/element-modeles/guide-model';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { DEFAULT_GUIDE_COLOR } from '@com/guides-line/guides-color-panel';
import { BaseGuidesService } from '@src/slide/application/service/guides-manager/base-guides-service';
import { IAddGuideProps } from '@src/slide/application/service/guides-manager/base-guides-service.interface';

interface ISlideGuidesServiceProps {
    selectionService: ISelectionService;
}

/**
 * slide参考线
 */
export default class SlideGuidesService extends BaseGuidesService implements ISlideGuidesService {
    public constructor(props: ISlideGuidesServiceProps) {
        super(props);
    }

    /**
     * 增加一根垂直参考线
     */
    public addVertGuides = (props: IAddGuideProps) => {
        const { currentGuide } = props;
        const pos = this.computeAddVertPosition(this.vertGuides, props);
        if (pos > this.slideWidth || pos < 0) return;
        const guideModel = new Guide();
        guideModel.orient = EPpGuideOrientation.ppVerticalGuide;
        guideModel.color = currentGuide ? currentGuide.color : DEFAULT_GUIDE_COLOR;
        guideModel.pos = pos;
        this.vertGuides.push(guideModel);
        this.updateGuidesProps();
    };

    /**
     * 增加一根水平参考线
     */
    public addHorzGuides = (props: IAddGuideProps) => {
        const { currentGuide } = props;
        const pos = this.computeAddHorzPosition(this.horzGuides, props);
        if (pos > this.slideHeight || pos < 0) return;
        const guideModel = new Guide();
        guideModel.orient = EPpGuideOrientation.ppHorizontalGuide;
        guideModel.color = currentGuide ? currentGuide.color : DEFAULT_GUIDE_COLOR;
        guideModel.pos = pos;
        this.horzGuides.push(guideModel);
        this.updateGuidesProps();
    };

    /**
     * 删除参考线
     */
    public deleteGuide = (guideModel?: IGuide) => {
        if (!guideModel) return;
        const { orient } = guideModel;
        if (orient === EPpGuideOrientation.ppVerticalGuide) {
            const index = this.vertGuides.findIndex(item => item === guideModel);
            if (index > -1) {
                this.vertGuides.splice(index, 1);
            }
        } else {
            const index = this.horzGuides.findIndex(item => item === guideModel);
            if (index > -1) {
                this.horzGuides.splice(index, 1);
            }
        }
        this.updateGuidesProps();
    };

    /**
     * 初始化参考线
     * @param guides sdk数据
     */
    public initGuides = (guides: Array<IGuide>) => {
        transaction(() => {
            // 如果是空数据，就初始化两个默认参考线，并跳过后续
            guides.forEach((guide) => {
                const { orient = EPpGuideOrientation.ppVerticalGuide, color } = guide;
                const guideModel = new Guide();
                guideModel.pos = Math.floor(guide.pos / 4);
                guideModel.orient = orient;
                guideModel.color = color;

                // 过滤下重复数据
                if (orient === EPpGuideOrientation.ppVerticalGuide) {
                    const index = this.vertGuides.findIndex(guide => guide.pos === guideModel.pos);
                    if (index === -1) this.vertGuides.push(guideModel);
                } else {
                    const index = this.horzGuides.findIndex(guide => guide.pos === guideModel.pos);
                    if (index === -1) this.horzGuides.push(guideModel);
                }
            });
        });
    };

    /**
     * 更新数据
     */
    public updateGuidesProps = (): void => {
        const slideGuides = this.getUpdateProps();
        this.guideModel?.updateGuidesPropsNoUndo({
            slideGuides,
            masterGuides: this.guideModel.masterGuides,
            layoutGuides: this.guideModel.layoutGuides,
        }, EUpdateGuidesType.slide);
    };

    public updateGuidesPosWhenCanvasSizeChange = (changeVertRadio, changeHorzRadio) => {
        if (changeVertRadio || changeHorzRadio) {
            this.vertGuides.forEach((guide) => {
                guide.pos = this.fixVertPos(guide.pos * changeVertRadio);
            });
            this.horzGuides.forEach((guide) => {
                guide.pos = this.fixHorzPos(guide.pos * changeHorzRadio);
            });
        }
    };

    public getUpdateProps = () => [...this.vertGuides, ...this.horzGuides].map(guide => ({
        color: guide.color,
        pos: guide.pos * 4,
        orient: guide.orient,
    }));
}
